import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { apiService } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Upload,
  FileText,
  FileDown,
  Loader2,
  Plus,
  Trash2,
  Sparkles,
  AlertTriangle,
} from "lucide-react";

// ── Amount → words (GHS), mirrors backend utils.ecobank_letter for a live
// preview only. The generated document always uses the backend as the
// authoritative source; this is purely UI feedback.
const ONES = [
  "ZERO", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE",
  "TEN", "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN",
  "SEVENTEEN", "EIGHTEEN", "NINETEEN",
];
const TENS = ["", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY", "EIGHTY", "NINETY"];
const SCALES = [[1e12, "TRILLION"], [1e9, "BILLION"], [1e6, "MILLION"], [1e3, "THOUSAND"]];

function threeDigits(n) {
  const parts = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds) parts.push(`${ONES[hundreds]} HUNDRED`);
  if (rest) {
    if (hundreds) parts.push("AND");
    if (rest < 20) parts.push(ONES[rest]);
    else {
      const t = Math.floor(rest / 10);
      const o = rest % 10;
      parts.push(o ? `${TENS[t]}-${ONES[o]}` : TENS[t]);
    }
  }
  return parts.join(" ");
}

function integerToWords(n) {
  if (n === 0) return "ZERO";
  let remainder = n;
  const chunks = [];
  for (const [value, name] of SCALES) {
    if (remainder >= value) {
      chunks.push(`${threeDigits(Math.floor(remainder / value))} ${name}`);
      remainder %= value;
    }
  }
  if (remainder) {
    if (chunks.length && remainder < 100) chunks.push("AND");
    chunks.push(threeDigits(remainder));
  }
  return chunks.join(" ");
}

function amountToWordsGhs(amount) {
  const rounded = Math.round(amount * 100) / 100;
  const cedis = Math.floor(rounded);
  const pesewas = Math.round((rounded - cedis) * 100);
  const cediUnit = cedis === 1 ? "GHANA CEDI" : "GHANA CEDIS";
  let words = `${integerToWords(cedis)} ${cediUnit}`;
  if (pesewas) {
    const pesewaUnit = pesewas === 1 ? "PESEWA" : "PESEWAS";
    words += ` AND ${integerToWords(pesewas)} ${pesewaUnit}`;
  }
  return `${words} ONLY.`;
}

function formatAmount(amount) {
  return (Math.round(amount * 100) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const emptyRow = (beneficiary = "") => ({
  reference: "",
  beneficiary,
  details: "",
  amount: "",
});

const today = () => new Date().toISOString().slice(0, 10);

const FileDrop = ({ label, file, onFile }) => {
  const inputRef = useRef(null);
  return (
    <div>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="mt-2 w-full flex items-center gap-3 rounded-xl border-2 border-dashed border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors px-4 py-4 text-left"
      >
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          {file ? <FileText className="w-5 h-5 text-emerald-600" /> : <Upload className="w-5 h-5 text-slate-400" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">
            {file ? file.name : "Click to select a PDF or image"}
          </p>
          <p className="text-xs text-slate-500">
            {file ? `${(file.size / 1024).toFixed(0)} KB` : "GRA / SSNIT invoice"}
          </p>
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/*"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0] || null)}
      />
    </div>
  );
};

const EcobankLetterPage = () => {
  const [graFile, setGraFile] = useState(null);
  const [ssnitFile, setSsnitFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [generating, setGenerating] = useState(null); // 'pdf' | 'docx' | null

  const [form, setForm] = useState({
    letter_date: today(),
    attention_name: "Ms. Francisca Akubor",
    attention_department: "EGH Consumer Banking",
    bank_name: "Ecobank Ghana PLC",
    branch_address: "",
    bank_city: "Accra, Ghana",
    account_number: "1441005143618",
    account_name: "EDOMATCH GHANA LTD",
    period_label: "",
    signatory_name: "Jelle Verzijden",
    signatory_title: "",
    signatory_org: "Edomatch Ghana",
    signatory_email: "j.verzijden@edomatch.com",
    signatory_phone: "+31 6 24 606 481",
    rows: [
      emptyRow("Ghana Revenue Authority (GRA)"),
      emptyRow("Social Security and National Insurance Trust (SSNIT)"),
    ],
  });

  // Load server-side defaults so the static fields always match the backend.
  useEffect(() => {
    apiService
      .getEcobankDefaults()
      .then(({ data }) => {
        setForm((prev) => ({
          ...prev,
          attention_name: data.attention_name ?? prev.attention_name,
          attention_department: data.attention_department ?? prev.attention_department,
          bank_name: data.bank_name ?? prev.bank_name,
          branch_address: data.branch_address ?? prev.branch_address,
          bank_city: data.bank_city ?? prev.bank_city,
          account_number: data.account_number ?? prev.account_number,
          account_name: data.account_name ?? prev.account_name,
          signatory_name: data.signatory_name ?? prev.signatory_name,
          signatory_title: data.signatory_title ?? prev.signatory_title,
          signatory_org: data.signatory_org ?? prev.signatory_org,
          signatory_email: data.signatory_email ?? prev.signatory_email,
          signatory_phone: data.signatory_phone ?? prev.signatory_phone,
        }));
      })
      .catch(() => {
        /* Non-fatal: hard-coded defaults above already cover this. */
      });
  }, []);

  const total = useMemo(
    () => form.rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0),
    [form.rows]
  );
  const totalWords = useMemo(() => amountToWordsGhs(total), [total]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setRow = (idx, key, value) =>
    setForm((prev) => ({
      ...prev,
      rows: prev.rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
    }));
  const addRow = () => setForm((prev) => ({ ...prev, rows: [...prev.rows, emptyRow()] }));
  const removeRow = (idx) =>
    setForm((prev) => ({ ...prev, rows: prev.rows.filter((_, i) => i !== idx) }));

  const handleExtract = async () => {
    if (!graFile && !ssnitFile) {
      toast.error("Select at least one invoice to read.");
      return;
    }
    setExtracting(true);
    try {
      const { data } = await apiService.extractEcobankInvoices(graFile, ssnitFile);
      const byKind = Object.fromEntries((data.rows || []).map((r) => [r.kind, r]));

      setForm((prev) => {
        const rows = prev.rows.map((row, idx) => {
          // Row 0 = GRA, row 1 = SSNIT (default layout).
          const kind = idx === 0 ? "gra_paye" : idx === 1 ? "ssnit" : null;
          const extracted = kind ? byKind[kind] : null;
          if (!extracted) return row;
          return {
            reference: extracted.reference || row.reference,
            beneficiary: extracted.beneficiary || row.beneficiary,
            details: extracted.details || row.details,
            amount: extracted.amount || row.amount,
          };
        });
        return {
          ...prev,
          rows,
          period_label: prev.period_label || data.period_label || "",
        };
      });

      (data.warnings || []).forEach((w) => toast.warning(w));
      const filled = (data.rows || []).length;
      if (filled) toast.success(`Read ${filled} invoice${filled === 1 ? "" : "s"}. Please verify the details.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read the invoices.");
    } finally {
      setExtracting(false);
    }
  };

  const buildPayload = () => ({
    letter_date: form.letter_date,
    attention_name: form.attention_name,
    attention_department: form.attention_department,
    bank_name: form.bank_name,
    branch_address: form.branch_address,
    bank_city: form.bank_city,
    account_number: form.account_number,
    account_name: form.account_name,
    period_label: form.period_label,
    signatory_name: form.signatory_name,
    signatory_title: form.signatory_title,
    signatory_org: form.signatory_org,
    signatory_email: form.signatory_email,
    signatory_phone: form.signatory_phone,
    rows: form.rows
      .filter((r) => r.reference && r.beneficiary && r.details && parseFloat(r.amount) > 0)
      .map((r) => ({
        reference: r.reference,
        beneficiary: r.beneficiary,
        details: r.details,
        amount: parseFloat(r.amount),
      })),
    enclosures: form.rows
      .filter((r) => r.reference && parseFloat(r.amount) > 0)
      .map((r) => `${r.beneficiary} — ${r.reference} (${form.period_label}, GHS ${formatAmount(parseFloat(r.amount))})`),
  });

  const canGenerate =
    !!form.period_label &&
    form.rows.some((r) => r.reference && r.beneficiary && r.details && parseFloat(r.amount) > 0);

  const handleGenerate = async (format) => {
    if (!canGenerate) {
      toast.error("Fill the period and at least one complete invoice row first.");
      return;
    }
    setGenerating(format);
    try {
      const payload = buildPayload();
      const { data } = await apiService.generateEcobankLetter(payload, format);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `EdoMatch_Ecobank_PaymentInstruction_${form.letter_date}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} generated.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate the letter.");
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Ecobank Payment Instruction</h1>
        <p className="text-sm text-slate-500 mt-1">
          Upload the monthly GRA (PAYE) and SSNIT invoices, verify the details, then download the
          signed-ready instruction letter as PDF and Word.
        </p>
      </div>

      {/* Step 1 — upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">1. Upload invoices</CardTitle>
          <CardDescription>The AI reads the reference, amount and period off each invoice.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FileDrop label="GRA PAYE invoice" file={graFile} onFile={setGraFile} />
            <FileDrop label="SSNIT contribution advice" file={ssnitFile} onFile={setSsnitFile} />
          </div>
          <Button onClick={handleExtract} disabled={extracting} className="bg-emerald-600 hover:bg-emerald-700">
            {extracting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {extracting ? "Reading…" : "Read invoices"}
          </Button>
        </CardContent>
      </Card>

      {/* Step 2 — verify */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">2. Verify the letter</CardTitle>
          <CardDescription>Check every field. Nothing is sent — you download and sign it yourself.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Letter date</Label>
              <Input type="date" value={form.letter_date} onChange={(e) => setField("letter_date", e.target.value)} />
            </div>
            <div>
              <Label>Period (ending)</Label>
              <Input placeholder="e.g. April 2026" value={form.period_label} onChange={(e) => setField("period_label", e.target.value)} />
            </div>
            <div>
              <Label>Addressee</Label>
              <Input value={form.attention_name} onChange={(e) => setField("attention_name", e.target.value)} />
            </div>
            <div>
              <Label>Department</Label>
              <Input value={form.attention_department} onChange={(e) => setField("attention_department", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label>Branch address</Label>
              <Input value={form.branch_address} onChange={(e) => setField("branch_address", e.target.value)} placeholder="e.g. High Street Branch, Accra (leave blank to omit)" />
            </div>
            <div>
              <Label>Account number</Label>
              <Input value={form.account_number} onChange={(e) => setField("account_number", e.target.value)} />
            </div>
            <div>
              <Label>Account name</Label>
              <Input value={form.account_name} onChange={(e) => setField("account_name", e.target.value)} />
            </div>
          </div>

          {/* Invoice rows */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Invoices</Label>
              <Button type="button" variant="outline" size="sm" onClick={addRow}>
                <Plus className="w-4 h-4 mr-1" /> Add row
              </Button>
            </div>
            {form.rows.map((row, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 p-4 space-y-3 relative">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Invoice {idx + 1}</span>
                  {form.rows.length > 1 && (
                    <button type="button" onClick={() => removeRow(idx)} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label className="text-xs">Reference</Label>
                    <Input value={row.reference} onChange={(e) => setRow(idx, "reference", e.target.value)} placeholder="Invoice / reference no." />
                  </div>
                  <div>
                    <Label className="text-xs">Beneficiary</Label>
                    <Input value={row.beneficiary} onChange={(e) => setRow(idx, "beneficiary", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Amount (GHS)</Label>
                    <Input type="number" step="0.01" min="0" value={row.amount} onChange={(e) => setRow(idx, "amount", e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Reference / period details</Label>
                    <Textarea rows={3} value={row.details} onChange={(e) => setRow(idx, "details", e.target.value)} placeholder={"TIN / Employer no.\nScheme\nPeriod"} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals preview */}
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Total to be debited</span>
              <span className="text-lg font-semibold text-slate-900">GHS {formatAmount(total)}</span>
            </div>
            <p className="text-xs text-slate-500 mt-2">In words: {totalWords}</p>
          </div>

          {/* Signatory */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Signatory name</Label>
              <Input value={form.signatory_name} onChange={(e) => setField("signatory_name", e.target.value)} />
            </div>
            <div>
              <Label>Signatory title</Label>
              <Input value={form.signatory_title} onChange={(e) => setField("signatory_title", e.target.value)} placeholder="e.g. Co-Founder / CTO (leave blank to omit)" />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={form.signatory_org} onChange={(e) => setField("signatory_org", e.target.value)} />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.signatory_email} onChange={(e) => setField("signatory_email", e.target.value)} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.signatory_phone} onChange={(e) => setField("signatory_phone", e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 3 — download */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">3. Download</CardTitle>
          <CardDescription>Generate the instruction letter to print, sign and hand to Ecobank.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!canGenerate && (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <AlertTriangle className="w-4 h-4" />
              Fill the period and at least one complete invoice row to enable download.
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => handleGenerate("pdf")} disabled={!canGenerate || generating !== null} className="bg-emerald-600 hover:bg-emerald-700">
              {generating === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Download PDF
            </Button>
            <Button onClick={() => handleGenerate("docx")} disabled={!canGenerate || generating !== null} variant="outline">
              {generating === "docx" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Download Word
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EcobankLetterPage;
